import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const AREA_TYPES = [
  'plant',
  'unit',
  'area',
  'sub_area',
  'elevation',
  'grid',
  'zone',
  'train',
  'building',
  'floor',
  'room',
];

type Props = { projectId?: string };

export const WorkAreasPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const areasQ = useQuery({
    queryKey: ['cep-areas', projectId],
    queryFn: () => epcService.getWorkAreas(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createWorkArea({ ...values, projectId }),
    onSuccess: () => {
      message.success('Construction area created');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-areas'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) {
    return <Tag>Select a project to manage Construction Areas</Tag>;
  }

  const rows = (areasQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={() => setOpen(true)}>
          New Construction Area
        </Button>
        <Tag color="geekblue">CEP Wave 1</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={areasQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Code', dataIndex: 'code', width: 120 },
          { title: 'Title', dataIndex: 'title' },
          { title: 'Type', dataIndex: 'area_type', width: 110 },
          {
            title: 'Parent',
            dataIndex: 'parent_id',
            width: 120,
            render: (v: string) =>
              v ? rows.find((r) => String(r.id) === String(v))?.code || v.slice(0, 8) : '—',
          },
        ]}
      />
      <Modal
        title="New Construction Area"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => createMut.mutate(values)}
          initialValues={{ areaType: 'area' }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="areaType" label="Area type">
            <Select options={AREA_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="parentId" label="Parent area">
            <Select
              allowClear
              options={rows.map((a: any) => ({
                value: String(a.id),
                label: `${a.code} — ${a.title}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkAreasPanel;
